import { singleton } from 'tsyringe';

export interface InputState {
	moveLeft: boolean;
	moveRight: boolean;
	moveUp: boolean;
	moveDown: boolean;
	jump: boolean;
	jumpJustPressed: boolean;
	attack: boolean;
	poop: boolean;
	poopJustPressed: boolean;
	crouch: boolean;
	slowWalk: boolean;
	horizontalAxis: number;
	verticalAxis: number;
}

@singleton()
export class InputManager {
	private lastJumpState: boolean = false;
	private lastPoopState: boolean = false;
	private cachedGamepad: Gamepad | null = null;
	private keysPressed: Set<string> = new Set();

	// Frame-based caching
	private cachedState: InputState | null = null;
	private lastFrameCount: number = -1;

	constructor() {
		this.initializeKeyboard();
		this.initializeGamepad();
	}

	private initializeKeyboard(): void {
		window.addEventListener('keydown', (e) => {
			this.keysPressed.add(e.code);
		});
		window.addEventListener('keyup', (e) => {
			this.keysPressed.delete(e.code);
		});
		window.addEventListener('blur', () => {
			this.keysPressed.clear();
		});
	}

	private initializeGamepad(): void {
		window.addEventListener('gamepadconnected', () => {
			this.refreshGamepad();
		});
		window.addEventListener('gamepaddisconnected', () => {
			this.cachedGamepad = null;
		});
	}

	private refreshGamepad(): void {
		if (navigator.getGamepads) {
			const gamepads = navigator.getGamepads();
			this.cachedGamepad = gamepads[0] || null;
		}
	}

	private isKey(code: string): boolean {
		return this.keysPressed.has(code);
	}

	getInputState(): InputState {
		// Return cached state if already computed this frame
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const currentFrame = (window as any).frameCount || 0;
		if (this.cachedState && this.lastFrameCount === currentFrame) {
			return this.cachedState;
		}

		const state: InputState = {
			moveLeft: false,
			moveRight: false,
			moveUp: false,
			moveDown: false,
			jump: false,
			jumpJustPressed: false,
			attack: false,
			poop: false,
			poopJustPressed: false,
			crouch: false,
			slowWalk: false,
			horizontalAxis: 0,
			verticalAxis: 0,
		};

		// Keyboard input (single source of truth)
		state.moveLeft = this.isKey('ArrowLeft');
		state.moveRight = this.isKey('ArrowRight');
		state.moveUp = this.isKey('ArrowUp');
		state.moveDown = this.isKey('ArrowDown');
		state.crouch = this.isKey('ArrowDown');
		state.attack = this.isKey('KeyD');
		state.poop = this.isKey('KeyX');
		state.slowWalk = this.isKey('ShiftLeft') || this.isKey('ShiftRight');
		state.jump = this.isKey('ArrowUp') || this.isKey('Space');

		// Gamepad input (refresh every frame)
		this.refreshGamepad();

		if (this.cachedGamepad) {
			const gp = this.cachedGamepad;

			// Attack (X button)
			if (gp.buttons[2]?.pressed) {
				state.attack = true;
			}

			// Poop (Y/Triangle button)
			if (gp.buttons[3]?.pressed) {
				state.poop = true;
			}

			// Analog stick
			state.horizontalAxis = gp.axes[0] || 0;
			state.verticalAxis = gp.axes[1] || 0;

			if (Math.abs(state.horizontalAxis) > 0.15) {
				state.moveLeft = state.moveLeft || state.horizontalAxis < 0;
				state.moveRight = state.moveRight || state.horizontalAxis > 0;
			}

			if (state.verticalAxis > 0.15) {
				state.moveDown = true;
				state.crouch = true;
			}
			if (state.verticalAxis < -0.15) {
				state.moveUp = true;
			}

			// D-pad
			if (gp.buttons[12]?.pressed) state.moveUp = true;
			if (gp.buttons[13]?.pressed) {
				state.moveDown = true;
				state.crouch = true;
			}
			if (gp.buttons[14]?.pressed) state.moveLeft = true;
			if (gp.buttons[15]?.pressed) state.moveRight = true;

			// Jump (A button)
			if (gp.buttons[0]?.pressed) {
				state.jump = true;
			}
		}

		// Just pressed detection (combined keyboard + gamepad)
		state.jumpJustPressed = state.jump && !this.lastJumpState;
		this.lastJumpState = state.jump;

		state.poopJustPressed = state.poop && !this.lastPoopState;
		this.lastPoopState = state.poop;

		// Cache for this frame
		this.cachedState = state;
		this.lastFrameCount = currentFrame;

		return state;
	}

	isStartPressed(): boolean {
		return this.cachedGamepad?.buttons[9]?.pressed === true;
	}

	isEscapePressed(): boolean {
		return this.isKey('Escape');
	}
}
